/* logic-trainer-icons.js — pure SVG/HTML renderer for PuzzleSpec elements.
 *
 * NO DOM access — returns HTML strings the app injects. Renders:
 *   - shape elements {shape,color,size,fill,rot}
 *   - count elements {shape,count,...}  -> N small shapes
 *   - icon elements  {icon}             -> friendly emoji glyph
 *   - grid-shape options {cells}        -> polyomino on a grid
 *
 * Palette is the Okabe–Ito colour-blind-safe set; in colour-rule puzzles colour
 * is the differentiator, and everywhere else shape ALSO differs, so no puzzle is
 * solvable by colour alone. Every rendered element carries an aria-label.
 *
 * Exposes (UMD): window.LogicTrainerIcons / module.exports.
 */
(function (root, factory) {
  'use strict';
  var I = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = I;
  if (typeof window !== 'undefined') window.LogicTrainerIcons = I;
  else if (root) root.LogicTrainerIcons = I;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Vivid, name-true and mutually distinct (red≠orange≠yellow≠brown is the key
  // separation a kid needs). Shapes are the redundant cue everywhere except pure
  // colour-rule puzzles, and every element carries an aria-label colour name.
  var PALETTE = {
    red: '#E5322E', blue: '#1C6FB8', green: '#1FA34A', yellow: '#FFC400',
    orange: '#EE7700', purple: '#8E3FB0', pink: '#E84B8A', brown: '#8A5A2B'
  };
  var NEUTRAL = '#9aa3af';
  var EMOJI = {
    cat: '🐱', dog: '🐶', bird: '🐦', fish: '🐟', apple: '🍎',
    tree: '🌳', car: '🚗', ball: '⚽', sun: '☀️', flower: '🌸'
  };
  var uid = 0;

  function colorHex(c) { return c === 'neutral' ? NEUTRAL : (PALETTE[c] || '#888'); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (m) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]; }); }

  // ── shape geometry (100x100 viewBox) ──
  function shapeGeom(shape, attrs) {
    switch (shape) {
      case 'circle': return '<circle cx="50" cy="50" r="40" ' + attrs + '/>';
      case 'square': return '<rect x="12" y="12" width="76" height="76" rx="10" ' + attrs + '/>';
      case 'triangle': return '<polygon points="50,10 90,86 10,86" ' + attrs + '/>';
      case 'diamond': return '<polygon points="50,8 92,50 50,92 8,50" ' + attrs + '/>';
      case 'hexagon': return '<polygon points="50,8 88,29 88,71 50,92 12,71 12,29" ' + attrs + '/>';
      case 'star': return '<polygon points="50,6 61,38 95,38 67,58 78,91 50,70 22,91 33,58 5,38 39,38" ' + attrs + '/>';
      case 'heart': return '<path d="M50 86C18 64 8 44 8 30 8 17 18 9 29 9c8 0 15 4 21 13 6-9 13-13 21-13 11 0 21 8 21 21 0 14-10 34-42 56z" ' + attrs + '/>';
      default: return '<circle cx="50" cy="50" r="40" ' + attrs + '/>';
    }
  }
  // fill variants: solid / outline / striped (hatch pattern, unique id per render)
  function fillAttrs(fill, hex, defsRef) {
    if (fill === 'outline') return { attrs: 'fill="none" stroke="' + hex + '" stroke-width="9"', defs: '' };
    if (fill === 'striped') {
      var id = 'ltp' + (++uid);
      var defs = '<defs><pattern id="' + id + '" width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">'
        + '<rect width="14" height="14" fill="' + hex + '" opacity="0.28"/><rect width="7" height="14" fill="' + hex + '"/></pattern></defs>';
      return { attrs: 'fill="url(#' + id + ')" stroke="' + hex + '" stroke-width="3"', defs: defs };
    }
    return { attrs: 'fill="' + hex + '"', defs: '' };
  }
  function oneShape(shape, color, fill, px, rot) {
    var hex = colorHex(color);
    var fa = fillAttrs(fill, hex);
    var g = shapeGeom(shape, fa.attrs);
    var rotAttr = rot ? ' transform="rotate(' + rot + ' 50 50)"' : '';
    return '<svg class="lt-shape" width="' + px + '" height="' + px + '" viewBox="0 0 100 100" aria-hidden="true">'
      + fa.defs + '<g' + rotAttr + '>' + g + '</g></svg>';
  }

  // colour swatch as an SVG (fill attribute, CSP-proof — inline style attrs can be
  // stripped by style-src). A wide rounded tile reads as "this colour", not a shape.
  function swatch(color, px) {
    return '<svg class="lt-shape" width="' + px + '" height="' + Math.round(px * 0.78) + '" viewBox="0 0 100 78" aria-hidden="true">'
      + '<rect x="4" y="4" width="92" height="70" rx="16" fill="' + colorHex(color) + '"/></svg>';
  }

  // ── grid-shape (polyomino) ──
  function renderGrid(cells, px) {
    var maxR = 0, maxC = 0, i;
    for (i = 0; i < cells.length; i++) { if (cells[i][0] > maxR) maxR = cells[i][0]; if (cells[i][1] > maxC) maxC = cells[i][1]; }
    var rows = maxR + 1, cols = maxC + 1, dim = Math.max(rows, cols);
    var unit = 100 / dim;
    var offR = (dim - rows) / 2, offC = (dim - cols) / 2;
    var rects = '';
    for (i = 0; i < cells.length; i++) {
      var x = (cells[i][1] + offC) * unit, y = (cells[i][0] + offR) * unit;
      rects += '<rect x="' + (x + 2) + '" y="' + (y + 2) + '" width="' + (unit - 4) + '" height="' + (unit - 4) + '" rx="3" fill="#2D7DD2"/>';
    }
    return '<svg class="lt-grid" width="' + px + '" height="' + px + '" viewBox="0 0 100 100" aria-hidden="true">' + rects + '</svg>';
  }

  // ── element dispatch ──
  function renderEl(el, opts) {
    opts = opts || {};
    var px = opts.px || 64;
    var label = opts.ariaLabel ? ' aria-label="' + esc(opts.ariaLabel) + '" role="img"' : ' aria-hidden="true"';
    if (el == null) return '';
    if (el.cells) return '<span class="lt-el"' + label + '>' + renderGrid(el.cells, px) + '</span>';
    if (el.icon) return '<span class="lt-el lt-emoji"' + label + ' style="font-size:' + Math.round(px * 0.72) + 'px;line-height:1">' + (EMOJI[el.icon] || '❓') + '</span>';
    if (el.count != null) {
      var per = el.count <= 4 ? el.count : Math.ceil(Math.sqrt(el.count));
      var mini = Math.max(14, Math.floor((px * 0.92) / Math.max(2, Math.ceil(el.count / per))));
      var inner = '';
      for (var k = 0; k < el.count; k++) inner += oneShape(el.shape, el.color, el.fill || 'solid', mini, 0);
      return '<span class="lt-el lt-count"' + label + ' style="max-width:' + px + 'px">'
        + '<span class="lt-count-grid" style="grid-template-columns:repeat(' + per + ',auto)">' + inner + '</span></span>';
    }
    var scale = el.size === 1 ? 0.6 : el.size === 3 ? 1.0 : 0.8;
    return '<span class="lt-el"' + label + '>' + oneShape(el.shape, el.color, el.fill || 'solid', Math.round(px * scale), el.rot || 0) + '</span>';
  }

  return {
    PALETTE: PALETTE, EMOJI: EMOJI, NEUTRAL: NEUTRAL,
    colorHex: colorHex,
    renderEl: renderEl, renderGrid: renderGrid, oneShape: oneShape, swatch: swatch
  };
});
